/* 🛡️ Battlegrounds — Feb 2026 Radial-Arena HUD
 * ----------------------------------------------------------
 * The tactical map is now the primary interaction surface.
 * The old text HUD (BreachLine + EnemyPlayers buttons) is fully retired —
 * clicking sprites on the map handles all combat interactions instead.
 *
 * Retained overlays (kept minimal, live above/below the map):
 *   • Queue join + drop-in controls (only when NOT in a match)
 *   • StatCards strip: MMR / Honor / Starlight / Carry-slow / Carts
 *   • Score bar + timers (warmup/active countdown)
 *   • Your HP bar (Poker Face — only you see this)
 *   • Starlight burn button
 *   • Announcement ticker + Honor leaderboard beneath the map
 *
 * ⚫ Neutral wildcard secrecy: no banner, no reveal text. The map shows a
 * "???" sprite and the kill toast stays cryptic ("…it fades").
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import BattlegroundsMap from "./BattlegroundsMap";
import { battleFx, streakTierFor } from "./BattleFx";
import VialRack from "./VialRack";
import PhoenixTierModal from "./PhoenixTierModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ────────────────────────── helpers ──────────────────────────
const fmtSecondsLeft = (isoStr) => {
  if (!isoStr) return null;
  const ms = new Date(isoStr).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 1000);
};
const mmss = (sec) => {
  if (sec == null || sec < 0) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function BattlegroundsPage() {
  const navigate = useNavigate();
  const [state, setState]                 = useState(null);
  const [cargo, setCargo]                 = useState(null);
  const [starlight, setStarlight]         = useState(null);
  const [playerState, setPlayerState]     = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [topHonor, setTopHonor]           = useState(null);
  const [tierModal, setTierModal]         = useState(null);  // {tierId, honor} | {atlas:true}
  const [busy, setBusy]                   = useState(false);
  const [tick, setTick]                   = useState(0);
  const [loadFailed, setLoadFailed]       = useState(false);

  // ─── data fetchers ───
  const refreshAll = useCallback(async () => {
    try {
      const [s, c, st, ps, ann, lb] = await Promise.allSettled([
        axios.get(`${API}/battlegrounds/state`),
        axios.get(`${API}/battlegrounds/cargo`),
        axios.get(`${API}/battlegrounds/starlight`),
        axios.get(`${API}/battlegrounds/state/me`),
        axios.get(`${API}/battlegrounds/announcements?limit=20`),
        axios.get(`${API}/battlegrounds/honor-leaderboard?limit=25`),
      ]);
      if (s.status === "fulfilled")   { setState(s.value.data); setLoadFailed(false); }
      else if (s.reason?.response?.status === 401) { setLoadFailed(true); }
      if (c.status === "fulfilled")   setCargo(c.value.data);
      if (st.status === "fulfilled")  setStarlight(st.value.data);
      if (ps.status === "fulfilled")  setPlayerState(ps.value.data);
      if (ann.status === "fulfilled") setAnnouncements(ann.value.data?.items || []);
      if (lb.status === "fulfilled")  setTopHonor(lb.value.data);
    } catch (e) {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);
  useEffect(() => {
    // Poll every 3s (map needs faster updates for movement), UI tick every 1s
    const tickerId = setInterval(() => setTick(t => t + 1), 1000);
    const pollId   = setInterval(refreshAll, 3000);
    return () => { clearInterval(tickerId); clearInterval(pollId); };
  }, [refreshAll]);

  // ─── derived ───
  const inQueue         = !!state?.queue?.in_queue;
  const match           = state?.active_match;
  const inWarmup        = match?.status === "warmup";
  const isActive        = match?.status === "active";
  const myTeam          = match?.viewer_team;
  const viewerHero = useMemo(() => {
    if (!match || !myTeam) return null;
    const heroes = match?.[`${myTeam}_team`]?.heroes || [];
    return heroes.find(h => h.is_viewer) || null;
  }, [match, myTeam]);
  const myHp     = viewerHero?.hp;
  const myMaxHp  = viewerHero?.max_hp;
  const myHpPct  = (myHp != null && myMaxHp) ? Math.round((myHp / myMaxHp) * 100) : null;
  const warmupSecLeft = useMemo(() => fmtSecondsLeft(match?.warmup_ends_at), [match, tick]);
  const matchSecLeft  = useMemo(() => fmtSecondsLeft(match?.match_ends_at || match?.expires_at), [match, tick]);
  const bubbleLeft    = useMemo(() => fmtSecondsLeft(playerState?.bubble_until_iso), [playerState, tick]);
  const respawnLeft   = useMemo(() => fmtSecondsLeft(playerState?.respawn_at_iso), [playerState, tick]);

  // ─── actions ───
  const onQueue = async () => {
    if (busy) return; setBusy(true);
    try {
      const r = await axios.post(`${API}/battlegrounds/queue`);
      if (r.data?.joined_existing) {
        toast.success(`🪂 Dropped into existing match on ${r.data.team?.toUpperCase()} team!`);
      } else if (r.data?.already_in_match) {
        toast.info("Already in a match.");
      } else {
        toast.success("⚔️ In the queue! Matchmaker fires every 10s.");
      }
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Queue join failed.");
    } finally { setBusy(false); }
  };

  const onLeave = async () => {
    if (busy) return; setBusy(true);
    try {
      await axios.post(`${API}/battlegrounds/queue/leave`);
      toast.success("🚪 Left the queue.");
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Leave failed.");
    } finally { setBusy(false); }
  };

  const onUseStarlight = async () => {
    if (busy || (starlight?.count || 0) <= 0) return;
    if (myHp == null || !myMaxHp) {
      toast.error("Can't read your HP yet — try again in a sec.");
      return;
    }
    if (myHp >= myMaxHp) {
      toast.info("Already at full HP — save your Starlight.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/battlegrounds/starlight/use`, {
        current_hp: myHp, max_hp: myMaxHp,
      });
      toast.success(`🌟 +${r.data.heal_amount} HP (${r.data.hp_before}→${r.data.hp_after})`);
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Heal failed.");
    } finally { setBusy(false); }
  };

  // ⚔️ Fire an attack at a target (called by the map when a sprite is clicked)
  const onAttack = async (targetUserId, targetName) => {
    if (busy) return; setBusy(true);
    try {
      const r = await axios.post(`${API}/battlegrounds/attack`, { target_user_id: targetUserId });
      const d = r.data;
      // ── 💥 Battle FX: floating damage number + optional streak banner ──
      try {
        // Locate the map DOM node + target position for the float coordinates
        const mapEl = document.querySelector('[data-testid="bg-arena-map"]');
        const rect  = mapEl?.getBoundingClientRect();
        // Find the target sprite by walking hero/npc lists
        let targetHero = null;
        for (const side of ["red_team", "blue_team"]) {
          const list = state?.active_match?.[side]?.heroes || [];
          const found = list.find(h => h.user_id === targetUserId);
          if (found) { targetHero = found; break; }
          const npcs = state?.active_match?.[side]?.npcs || [];
          const nfound = npcs.find(n => n.user_id === targetUserId);
          if (nfound) { targetHero = nfound; break; }
        }
        if (rect && targetHero) {
          const x = ((targetHero.pos_x ?? 50) / 100) * rect.width;
          const y = ((targetHero.pos_y ?? 50) / 100) * rect.height;
          battleFx.hit({
            x, y,
            damage: d.damage,
            isCrit: d.is_crit,
            isBlock: d.is_block,
            isKill: d.killed,
          });
        }
        // Streak banner on PvP KO with streak_count from kill_event
        if (d.killed && d.kill_event?.kind === "pvp_killed") {
          const cnt = d.kill_event?.streak_count || d.kill_event?.kill_streak || 0;
          const tier = streakTierFor(cnt);
          if (tier) battleFx.streak(tier.name, cnt);
        }
      } catch { /* fx are best-effort */ }
      const critTag = d.is_crit ? "💥 CRIT " : (d.is_block ? "🛡️ BLOCK " : "");
      if (d.killed) {
        const ev = d.kill_event || {};
        if (ev.kind === "pvp_killed") {
          toast.success(`⚔️ ${critTag}KO ${targetName} · +${ev.honor_to_killer}H · +100 score${ev.announcement ? " · " + ev.announcement.line : ""}`);
        } else if (ev.kind === "npc_killed") {
          toast.success(`⚔️ ${critTag}Downed ${targetName} · +${ev.score_added} score${ev.freeze_applied ? " · 🥶 DEPOSIT FREEZE" : ""}`);
        } else if (ev.kind === "phantom_killed") {
          toast(`…it fades.`);
        } else {
          toast.success(`⚔️ ${critTag}Downed ${targetName}!`);
        }
      } else {
        const emeraldHeal = d.emerald_heal ? ` · 💚+${d.emerald_heal}HP` : "";
        toast(`⚔️ ${critTag}${d.damage} → ${targetName}${emeraldHeal}`);
      }
      refreshAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Attack failed.";
      if (!/out of range|too fast|breach/i.test(detail)) {
        toast.error(detail);
      }
    } finally { setBusy(false); }
  };

  const onAfterMove = (moveData) => {
    // 🗺️ Optimistically bump the viewer's sprite position in the local match
    // state so the sprite visibly glides right away — otherwise it would only
    // update on the 3s poll and the user thinks "movement is broken".
    if (!moveData?.ok || moveData.pos_x == null || moveData.pos_y == null) return;
    setState(prev => {
      if (!prev?.active_match) return prev;
      const m = prev.active_match;
      const side = m.viewer_team;
      if (!side) return prev;
      const teamKey = `${side}_team`;
      const heroes = (m[teamKey]?.heroes || []).map(h =>
        h.is_viewer
          ? { ...h, pos_x: moveData.pos_x, pos_y: moveData.pos_y }
          : h);
      return {
        ...prev,
        active_match: {
          ...m,
          [teamKey]: { ...(m[teamKey] || {}), heroes },
        },
      };
    });
  };
  const onAfterPickup = () => { refreshAll(); };

  // 🏦 Manual "Dump Cargo" button — for lingering cargo from prior sessions,
  // or when the auto-deposit ring feels finicky. Best-effort: if in an active
  // match and too far from the T4, the server tells us and we surface it.
  const onDumpCargo = async () => {
    if (busy) return; setBusy(true);
    try {
      const r = await axios.post(`${API}/battlegrounds/cargo/deposit`);
      const d = r.data;
      toast.success(
        `🏦 Banked ${d.ambergris_deposited}A → +${d.chests_earned} chest${d.chests_earned === 1 ? "" : "s"}!` +
        (d.carryover_after > 0 ? ` (${d.carryover_after}A carries over)` : "")
      );
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deposit failed.");
    } finally { setBusy(false); }
  };

  if (!state && loadFailed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] via-[#10101A] to-black text-white flex items-center justify-center p-6">
        <div className="max-w-md text-center rounded-2xl border border-amber-700/40 bg-amber-950/20 p-8">
          <div className="text-5xl mb-3">🛡️</div>
          <div className="text-2xl font-black text-amber-300 mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
            BATTLEGROUNDS LOCKED
          </div>
          <p className="text-sm text-neutral-400 mb-5">
            Sign in to drop into a 10v10 cargo run with Roc — claim 30 🌟 Starlight on entry and start banking Honor.
          </p>
          <button data-testid="bg-sign-in"
            onClick={() => navigate("/")}
            className="px-6 py-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 text-white font-black tracking-wider">
            ⚔️ Sign in to enter
          </button>
        </div>
      </div>
    );
  }
  if (!state) return <div className="p-8 text-center text-neutral-500">Loading Battlegrounds…</div>;

  // ─── render ───
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0A0A] via-[#10101A] to-black text-white">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-baseline gap-3 mb-1">
          <button data-testid="bg-back" onClick={() => navigate(-1)}
            className="text-xs text-neutral-500 hover:text-neutral-300">← Back</button>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-wide mb-1"
            style={{ fontFamily: "Anton, sans-serif" }}>
          🛡️ <span className="text-amber-300">BATTLEGROUNDS</span>
        </h1>
        <p className="text-xs text-neutral-400 mb-5 max-w-2xl">
          10v10 squad PvP · 5-min warmup → 25-min play · win at 5000 score OR +2000 lead OR time-cap.
        </p>

        {/* 🧪 Vial Rack — quick pre-match drink strip (compact) */}
        <div className="mb-4">
          <VialRack compact />
        </div>

        {/* Stat strip — always shown while in a match */}
        {match && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
            <StatCard label="MMR" value={state.mmr} icon="📊" testid="bg-mmr" />
            <StatCard label="Honor" value={state.honor || 0} icon="🏅" color="amber" testid="bg-honor" />
            <StatCard
              label="🌟 Starlight"
              value={starlight?.count ?? "—"} icon="" color="cyan"
              testid="bg-starlight-count" />
            <StatCard
              label="🐌 Carry-Slow"
              value={`${cargo?.carry_slow_pct ?? 0}%`} icon=""
              color={cargo?.carry_slow_pct >= 60 ? "rose" : cargo?.carry_slow_pct > 0 ? "amber" : "neutral"}
              testid="bg-carry-slow" />
            <StatCard
              label="🛒 Carts"
              value={`${cargo?.carts_count ?? 0} (${cargo?.ambergris_carried ?? 0}A)`}
              icon="" color="neutral" testid="bg-carts" />
          </div>
        )}

        {/* 🏦 Manual DUMP CARGO button — always visible when carts > 0 */}
        {(cargo?.carts_count || 0) > 0 && (
          <button
            data-testid="bg-dump-cargo"
            onClick={onDumpCargo}
            disabled={busy}
            className="w-full mb-4 py-3 rounded-xl bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600 hover:from-amber-500 hover:via-amber-400 hover:to-amber-500 disabled:opacity-50 text-black font-black tracking-wider shadow-[0_0_20px_rgba(245,158,11,0.4)] border border-amber-300/60 animate-pulse">
            🏦 DUMP {cargo?.carts_count ?? 0} CARTS ({cargo?.ambergris_carried ?? 0}A)
            {match ? " — walk to your T4 first" : " → bank instantly (60A = 1 chest)"}
          </button>
        )}

        {/* MATCH BLOCK — Map is the primary UI */}
        {match ? (
          <div className="mb-5 rounded-2xl border-2 border-amber-700/40 bg-gradient-to-r from-red-950/20 via-black to-blue-950/20 p-4 sm:p-5">
            {/* Phase strip */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300/70">
                {inWarmup ? "⏱️ Warmup (joinable)" : isActive ? "⚔️ Active Match" : "Match"}
              </div>
              <div className="text-sm font-bold">
                {inWarmup && warmupSecLeft != null && (
                  <span className="text-amber-300" data-testid="bg-warmup-countdown">
                    🔓 Locks in {mmss(warmupSecLeft)}
                  </span>
                )}
                {isActive && matchSecLeft != null && (
                  <span className="text-red-300" data-testid="bg-match-countdown">
                    ⏳ Time {mmss(matchSecLeft)}
                  </span>
                )}
              </div>
            </div>

            {/* Score bars */}
            <ScoreBar
              redScore={match.red_team?.score || 0}
              blueScore={match.blue_team?.score || 0}
              redMmr={match.red_team?.avg_mmr}
              blueMmr={match.blue_team?.avg_mmr}
              winThreshold={match.win_conditions?.score_to_win || 5000}
              leadThreshold={match.win_conditions?.score_lead_to_win || 2000}
              myTeam={myTeam} />

            {/* 🗺️ The Radial Arena — replaces the text HUD entirely */}
            <div className="mt-4">
              <BattlegroundsMap
                match={match}
                myTeam={myTeam}
                viewerUserId={viewerHero?.user_id}
                cartsCount={cargo?.carts_count || 0}
                busy={busy}
                onAttack={onAttack}
                onAfterMove={onAfterMove}
                onAfterPickup={onAfterPickup}
              />
            </div>

            {/* Personal live state (below map) */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
              <MiniState label="🪂 Team"
                         value={myTeam ? myTeam.toUpperCase() : "—"}
                         tone={myTeam === "red" ? "rose" : myTeam === "blue" ? "blue" : "neutral"} />
              <MiniState label="🫧 Bubble"
                         value={bubbleLeft ? `${bubbleLeft}s` : (playerState?.bubble_active ? "yes" : "—")}
                         tone={playerState?.bubble_active ? "cyan" : "neutral"} />
              <MiniState label="🪦 Respawn"
                         value={respawnLeft && respawnLeft > 0 ? `${respawnLeft}s` : "—"}
                         tone={respawnLeft && respawnLeft > 0 ? "rose" : "neutral"} />
              <MiniState label="🥶 Deposit Freeze"
                         value={
                           match?.[`${myTeam}_team`]?.deposit_frozen_until_iso
                             ? `${fmtSecondsLeft(match[`${myTeam}_team`].deposit_frozen_until_iso) || 0}s`
                             : "—"
                         }
                         tone="neutral" />
            </div>

            {/* Your HP bar (Poker Face) */}
            {myHp != null && myMaxHp ? (
              <div className="mt-3" data-testid="bg-my-hp">
                <div className="flex justify-between text-[10px] mb-1">
                  <span className="text-neutral-400 uppercase tracking-widest">
                    🩺 Your HP <span className="text-[9px] opacity-50">(only you see this)</span>
                  </span>
                  <span className={`font-black ${myHpPct < 30 ? "text-rose-400" : myHpPct < 60 ? "text-amber-300" : "text-emerald-300"}`}>
                    {myHp}/{myMaxHp} · {myHpPct}%
                  </span>
                </div>
                <div className="h-2.5 bg-neutral-900 rounded-full overflow-hidden border border-neutral-800">
                  <div className={`h-full transition-all duration-500 ${
                       myHpPct < 30 ? "bg-gradient-to-r from-rose-700 to-rose-500"
                                    : myHpPct < 60 ? "bg-gradient-to-r from-amber-700 to-amber-500"
                                                    : "bg-gradient-to-r from-emerald-700 to-emerald-500"}`}
                       style={{ width: `${myHpPct}%` }} />
                </div>
              </div>
            ) : null}

            {/* Starlight burn */}
            {(starlight?.count || 0) > 0 && (
              <button data-testid="bg-use-starlight"
                onClick={onUseStarlight}
                disabled={busy || (myHp != null && myMaxHp && myHp >= myMaxHp)}
                className="mt-4 w-full py-2.5 rounded-xl bg-gradient-to-br from-cyan-700 to-cyan-900 hover:from-cyan-600 hover:to-cyan-800 text-cyan-50 font-bold disabled:opacity-50 disabled:cursor-not-allowed">
                {myHp != null && myMaxHp && myHp >= myMaxHp
                  ? `🌟 Full HP — ${starlight.count} Starlight banked`
                  : `🌟 Spend 1 Starlight → heal +25% HP (~${myMaxHp ? Math.floor(myMaxHp * 0.25) : "?"}) · ${starlight.count} left`}
              </button>
            )}
          </div>
        ) : (
          // Queue-only view (no active match)
          <div className="mb-5 rounded-2xl border border-amber-700/40 bg-gradient-to-br from-amber-950/20 via-black to-black p-6">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-lg font-black text-amber-300">Battleground Queue</div>
                <div className="text-xs text-neutral-400 mt-1">
                  1+ player fires a match instantly · 30 🌟 Starlight on start · 5-min warmup joinable window
                </div>
                {inQueue && (
                  <div className="text-xs text-emerald-300 mt-2" data-testid="bg-queue-position">
                    🔄 In queue (#{state.queue?.queue_position} of {state.queue?.queue_total}).
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                {inQueue ? (
                  <button data-testid="bg-leave-queue" onClick={onLeave} disabled={busy}
                    className="px-5 py-3 rounded-lg bg-neutral-800 hover:bg-neutral-700 disabled:opacity-50 font-bold">
                    🚪 Leave
                  </button>
                ) : (
                  <button data-testid="bg-join-queue" onClick={onQueue} disabled={busy}
                    className="px-6 py-3 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 hover:from-amber-400 hover:to-amber-600 disabled:opacity-50 text-white font-black tracking-wider shadow-lg shadow-amber-900/40">
                    ⚔️ {state?.queue?.queue_total ? "DROP IN" : "FIRE MATCH"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Ticker + Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-xl border border-violet-700/40 bg-black/40 p-5"
               data-testid="bg-ticker">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.3em] text-violet-300/70">📣 Live Callouts</div>
              <div className="text-[10px] text-neutral-500 italic">Newest first · 24h history</div>
            </div>
            {announcements.length === 0 ? (
              <div className="text-sm text-neutral-500 italic">Quiet on the wire. Land a kill streak and we&apos;ll see your name here.</div>
            ) : (
              <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {announcements.map((a) => (
                  <TickerLine key={a.ann_id} ann={a} />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-amber-700/40 bg-black/40 p-5"
               data-testid="bg-honor-leaderboard">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300/70">🏅 Honor Leaderboard</div>
              <button
                data-testid="phoenix-tier-atlas-open"
                onClick={() => setTierModal({ atlas: true })}
                className="text-[10px] text-neutral-500 italic hover:text-amber-300 underline decoration-dotted underline-offset-2"
                title="Open the full Phoenix Ascension ladder"
              >
                Top 25 · 🔥 Ascension Ladder
              </button>
            </div>
            {(!topHonor?.top || topHonor.top.length === 0) ? (
              <div className="text-sm text-neutral-500 italic">No combatants ranked yet. Be the first.</div>
            ) : (
              <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {topHonor.top.map((p) => (
                  <LeaderRow key={p.user_id} p={p} isViewer={p.is_viewer}
                             onTierClick={(id, h) => setTierModal({ tierId: id, honor: h })} />
                ))}
              </div>
            )}
            {topHonor?.viewer && !topHonor.viewer.is_in_top && (
              <div className="mt-3 pt-3 border-t border-neutral-800">
                <div className="text-[10px] text-neutral-500 mb-1.5">Your rank</div>
                <LeaderRow p={topHonor.viewer} isViewer={true}
                           onTierClick={(id, h) => setTierModal({ tierId: id, honor: h })} />
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 🔥 Phoenix Ascension lookup modal */}
      {tierModal && !tierModal.atlas && (
        <PhoenixTierModal
          tierId={tierModal.tierId}
          honor={tierModal.honor}
          onClose={() => setTierModal(null)}
        />
      )}
      {tierModal?.atlas && (
        <PhoenixAscensionAtlas onClose={() => setTierModal(null)}
                               onPick={(id) => setTierModal({ tierId: id })} />
      )}
    </div>
  );
}

// ────────────────────────── sub-components ──────────────────────────
function PhoenixAscensionAtlas({ onClose, onPick }) {
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/phoenix/tiers`).then(r => setTiers(r.data?.tiers || [])).catch(() => {});
  }, []);
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm p-4 flex items-start justify-center overflow-y-auto"
         onClick={onClose} data-testid="phoenix-atlas">
      <div className="w-full max-w-2xl mt-16 rounded-2xl border-2 border-amber-500/60 bg-[#0A0806] shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-neutral-800 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 font-black">Dragonious</div>
            <h2 className="font-black tracking-wide text-2xl md:text-3xl text-amber-100"
                style={{ fontFamily: "Anton, sans-serif" }}>🔥 Phoenix Ascension Ladder</h2>
            <p className="text-xs text-neutral-500 mt-1">Tap a tier for full lore.</p>
          </div>
          <button onClick={onClose}
                  className="w-8 h-8 rounded-full bg-black/60 hover:bg-black text-neutral-300 hover:text-white font-black"
                  data-testid="phoenix-atlas-close">×</button>
        </div>
        <div className="p-4 space-y-1.5 max-h-[70vh] overflow-y-auto">
          {tiers.map(t => (
            <button
              key={t.id}
              onClick={() => onPick(t.id)}
              data-testid={`phoenix-atlas-row-${t.id}`}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left hover:brightness-110 transition"
              style={{ borderColor: `${t.color}55`, background: `${t.color}12` }}
            >
              <div className="text-2xl">{t.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <div className="text-[10px] font-black uppercase tracking-widest" style={{ color: t.color }}>
                    #{t.rank}
                  </div>
                  <div className="font-black text-sm" style={{ color: t.color }}>{t.name}</div>
                </div>
                <div className="text-[10px] text-neutral-400 italic truncate">"{t.tagline}"</div>
              </div>
              <div className="text-[10px] text-neutral-500 tabular-nums">
                {t.min_honor.toLocaleString()}{t.max_honor != null ? `–${t.max_honor.toLocaleString()}` : "+"}🏅
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color = "amber", testid }) {
  const palette = {
    amber:   { border: "border-amber-600/40",   bg: "bg-amber-900/20",   text: "text-amber-300",  muted: "text-amber-400" },
    emerald: { border: "border-emerald-600/40", bg: "bg-emerald-900/20", text: "text-emerald-300",muted: "text-emerald-400" },
    cyan:    { border: "border-cyan-600/40",    bg: "bg-cyan-900/20",    text: "text-cyan-300",   muted: "text-cyan-400" },
    rose:    { border: "border-rose-600/40",    bg: "bg-rose-900/20",    text: "text-rose-300",   muted: "text-rose-400" },
    neutral: { border: "border-neutral-700",    bg: "bg-neutral-900/40", text: "text-neutral-200",muted: "text-neutral-500" },
  };
  const c = palette[color] || palette.amber;
  return (
    <div className={`px-3 py-2.5 rounded-xl border ${c.border} ${c.bg}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-wider ${c.muted}`}>{icon} {label}</div>
      <div className={`text-xl font-black ${c.text} mt-0.5`}>{value}</div>
    </div>
  );
}

function MiniState({ label, value, tone = "neutral" }) {
  const tones = {
    cyan:    "border-cyan-700/40 bg-cyan-900/20 text-cyan-300",
    rose:    "border-rose-700/40 bg-rose-900/20 text-rose-300",
    blue:    "border-blue-700/40 bg-blue-900/20 text-blue-300",
    neutral: "border-neutral-800 bg-neutral-900/40 text-neutral-300",
  };
  return (
    <div className={`px-2.5 py-2 rounded-lg border ${tones[tone] || tones.neutral} text-xs`}>
      <div className="opacity-70">{label}</div>
      <div className="font-black mt-0.5">{value}</div>
    </div>
  );
}

function ScoreBar({ redScore, blueScore, redMmr, blueMmr, winThreshold, leadThreshold, myTeam }) {
  const total = Math.max(1, redScore + blueScore);
  const redPct  = (redScore  / Math.max(redScore, blueScore, winThreshold)) * 100;
  const bluePct = (blueScore / Math.max(redScore, blueScore, winThreshold)) * 100;
  const lead    = Math.abs(redScore - blueScore);
  return (
    <div data-testid="bg-score-bar" className="space-y-2">
      <div>
        <div className="flex justify-between text-[11px] mb-0.5">
          <span className={`font-bold ${myTeam === "red" ? "text-red-300 underline" : "text-red-300/70"}`}>
            🔴 Red {redMmr ? `(${redMmr}MMR)` : ""}
          </span>
          <span className="font-black text-red-300">{redScore} pts</span>
        </div>
        <div className="h-2 bg-neutral-900 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-all duration-700"
               style={{ width: `${Math.min(100, redPct)}%` }} />
        </div>
      </div>
      <div>
        <div className="flex justify-between text-[11px] mb-0.5">
          <span className={`font-bold ${myTeam === "blue" ? "text-blue-300 underline" : "text-blue-300/70"}`}>
            🔵 Blue {blueMmr ? `(${blueMmr}MMR)` : ""}
          </span>
          <span className="font-black text-blue-300">{blueScore} pts</span>
        </div>
        <div className="h-2 bg-neutral-900 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-600 to-blue-400 transition-all duration-700"
               style={{ width: `${Math.min(100, bluePct)}%` }} />
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-neutral-500 pt-1">
        <span>Lead: <b className="text-neutral-300">{lead}</b> / {leadThreshold} to auto-win</span>
        <span>Score-to-win: <b className="text-neutral-300">{winThreshold}</b></span>
        <span>Total: <b>{total}</b></span>
      </div>
    </div>
  );
}

function TickerLine({ ann }) {
  const kind = ann.kind || "info";
  const colorMap = {
    rampage:      "border-orange-700/40 bg-orange-900/20 text-orange-200",
    shutdown:     "border-emerald-700/40 bg-emerald-900/20 text-emerald-200",
    elite_kill:   "border-amber-700/40 bg-amber-900/20 text-amber-200",
    match_start:  "border-neutral-700 bg-neutral-900/40 text-neutral-300",
  };
  return (
    <div className={`px-2.5 py-1.5 rounded-md border text-[12px] ${colorMap[kind] || colorMap.match_start}`}
         data-testid={`ann-${ann.ann_id}`}>
      {ann.line}
    </div>
  );
}

function LeaderRow({ p, isViewer, onTierClick }) {
  return (
    <div data-testid={`leader-rank-${p.rank}`}
         className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border ${
           isViewer ? "border-amber-500 bg-amber-900/30" : "border-neutral-800 bg-black/40"}`}>
      <div className={`text-sm font-black w-7 text-center ${isViewer ? "text-amber-200" : "text-amber-400"}`}>
        #{p.rank}
      </div>
      <div className="text-base">{p.icon}</div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold truncate ${isViewer ? "text-amber-100" : ""}`}>
          {p.name}{isViewer ? " (you)" : ""}
          {p.pvp_win_streak >= 3 && (
            <span className="ml-1.5 text-[10px] text-orange-400 font-black">
              🔥 {p.pvp_win_streak}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {/* 🔥 Phoenix Ascension tier banner — click for lore */}
          {p.tier_name && (
            <button
              type="button"
              data-testid={`phoenix-tier-banner-${p.rank}`}
              onClick={(e) => { e.stopPropagation(); onTierClick?.(p.tier_id, p.honor); }}
              title={`${p.tier_name} — tap for lore`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-wider hover:brightness-125 transition"
              style={{ borderColor: `${p.tier_color}80`, color: p.tier_color,
                        background: `${p.tier_color}18` }}
            >
              <span>{p.tier_icon}</span>
              <span className="truncate max-w-[110px]">{p.tier_name}</span>
            </button>
          )}
          <div className="text-[10px] text-neutral-500">
            {p.bg_pvp_kills}K · {p.bg_npc_kills}N · {p.bg_mmr}MMR
          </div>
        </div>
      </div>
      <div className="text-amber-200 font-black text-sm">{p.honor || 0}🏅</div>
    </div>
  );
}
